import axiosInstance from "../Interceptors/axiosInstance.jsx";
import Cookies from "js-cookie";
import { fetchUserProfile } from "./newUserService";
import { normalizeProfileResult } from "../utils/profileUtils";
import {
  forceDraftStatusOnFormData,
  isSuperAdminRole,
  preserveStatusOnFormData,
} from "../utils/articleApprovalWorkflow";
import { resolveEventStatus } from "../components/ExternalEvents/eventStatusUtils";
import { normalizeEventApiItem } from "../components/ExternalEvents/eventFormUtils";
import { normalizeEventListItem } from "../components/ExternalEvents/eventListUtils";

const getMultipartHeaders = () => ({
  "Content-Type": "multipart/form-data",
  Authorization: `Bearer ${Cookies.get("accessToken")}`,
});

const headers = {
  accept: "*/*",
  Authorization: `Bearer ${Cookies.get("accessToken")}`,
  "Content-Type": "application/json",
};

let cachedIsSuperAdmin = null;

// Event approval workflow (mirrors article approval workflow)
const resolveIsSuperAdmin = async () => {
  if (cachedIsSuperAdmin !== null) {
    return cachedIsSuperAdmin;
  }

  const response = await fetchUserProfile();
  const profile = normalizeProfileResult(response.data?.result);
  cachedIsSuperAdmin = isSuperAdminRole(profile?.roleName);
  return cachedIsSuperAdmin;
};

const getEventIdFromFormData = (formData) =>
  formData.get("id") || formData.get("Id");

export const getExternalEventsFilterList = async (payload) => {
  const response = await axiosInstance.post(
    "external-events/filter",
    payload,
    { headers }
  );
  const items = response.data?.result?.items;

  if (Array.isArray(items)) {
    return {
      ...response,
      data: {
        ...response.data,
        result: {
          ...response.data.result,
          items: items.map((item) => normalizeEventListItem(item)),
        },
      },
    };
  }

  return response;
};

export const getExternalEventById = async (id) => {
  const response = await axiosInstance.get(`external-events/get/${id}`, {
    headers,
  });
  return normalizeEventApiItem(response.data.result);
};

export const addExternalEvent = async (formData) => {
  // Event approval workflow
  const isSuperAdmin = await resolveIsSuperAdmin();
  const payload = isSuperAdmin
    ? formData
    : forceDraftStatusOnFormData(formData);

  return await axiosInstance.post("external-events/add", payload, {
    headers: getMultipartHeaders(),
  });
};

export const updateExternalEvent = async (formData) => {
  // Event approval workflow
  const isSuperAdmin = await resolveIsSuperAdmin();
  let payload = formData;

  if (!isSuperAdmin) {
    const eventId = getEventIdFromFormData(formData);
    if (eventId) {
      const existing = await getExternalEventById(eventId);
      const existingStatus = resolveEventStatus(existing);
      payload = preserveStatusOnFormData(formData, existingStatus);
    } else {
      payload = forceDraftStatusOnFormData(formData);
    }
  }

  return await axiosInstance.put("external-events/update", payload, {
    headers: getMultipartHeaders(),
  });
};

export const deleteExternalEvent = async (id) => {
  return await axiosInstance.delete(`external-events/delete/${id}`, {
    headers,
  });
};

export const changeExternalEventStatus = async ({ id, status }) => {
  // Event approval workflow
  const isSuperAdmin = await resolveIsSuperAdmin();
  if (!isSuperAdmin) {
    const error = new Error("Forbidden");
    error.response = {
      status: 403,
      data: {
        isSuccess: false,
        message: "Only Super Admin can publish or unpublish events.",
      },
    };
    throw error;
  }

  return await axiosInstance.patch(
    "external-events/change-status",
    {
      id,
      Id: id,
      status,
      Status: status,
    },
    { headers }
  );
};
